import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Chip, CircularProgress, Button, Divider, Paper,
  Grid, Alert, TextField, MenuItem, Card, CardContent, IconButton, Skeleton,
} from "@mui/material";
import ArrowBackRoundedIcon    from "@mui/icons-material/ArrowBackRounded";
import HeadsetMicRoundedIcon   from "@mui/icons-material/HeadsetMicRounded";
import CheckCircleRoundedIcon  from "@mui/icons-material/CheckCircleRounded";
import PersonRoundedIcon       from "@mui/icons-material/PersonRounded";
import OpenInNewRoundedIcon    from "@mui/icons-material/OpenInNewRounded";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";
import { encryptId } from "../helper/idCodec.js"; // eslint-disable-line no-unused-vars
import ResolutionModal from "./ResolutionModal.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:"#007C84", tealDk:"#016268", tealLt:"#E0F4F5",
  orange:"#FF7900", orangeLt:"#FFF0E0",
  emerald:"#059669", emeraldLt:"#D1FAE5",
  rose:"#E11D48", roseLt:"#FFE4E6",
  amber:"#D97706", amberLt:"#FEF3C7",
  indigo:"#4F46E5", indigoLt:"#EEF2FF",
  violet:"#7C3AED", violetLt:"#EDE9FE",
  bg:"#F7FAFC", border:"#E2E8F0", text:"#0F172A", muted:"#64748B",
};

const STATUS_COLORS = {
  submitted: "warning",
  under_review: "info",
  awaiting_response: "secondary",
  resolved: "success",
  closed: "default",
  reopened: "error",
  escalated: "error",
};

const CATEGORY_LABELS = {
  damaged_goods: "Damaged Goods",
  late_delivery: "Late Delivery",
  driver_behaviour: "Driver Behaviour",
  billing_dispute: "Billing Dispute",
  lost_package: "Lost Package",
  payment_dispute: "Payment Dispute",
  missing_goods: "Missing Goods",
  delayed_delivery: "Delayed Delivery",
  booking_cancellation: "Booking Cancellation",
  other: "Other",
};

const OUTCOME_LABELS = {
  R1: "Full Refund to Customer",
  R2: "Partial Refund to Customer",
  R3: "Owner/Driver Deduction",
  R4: "No Action — Complaint Invalid",
  R5: "Mutual Agreement",
  R6: "External Escalation",
};

// CMP-002 — actor color cues for the audit timeline.
const ACTOR_COLORS = {
  customer: "#2563EB",
  owner: "#D97706",
  driver: "#F59E0B",
  admin: "#7C3AED",
  system: "#6B7280",
};

const TARGET_OPTIONS = [
  { value: "customer", label: "Customer" },
  { value: "owner_driver", label: "Owner / Driver" },
  { value: "both", label: "Both parties" },
];

export default function ComplaintDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [complaint, setComplaint] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showResolutionModal, setShowResolutionModal] = useState(false);

  // Request-details form state.
  const [requestTarget, setRequestTarget] = useState("owner_driver");
  const [requestMessage, setRequestMessage] = useState("");

  const fetchComplaint = useCallback(async () => {
    setLoading(true);
    try {
      const url = API_ENDPOINTS.getComplaintById.replace(":complaint_id", id);
      const res = await getData(url);
      if (res?.result) setComplaint(res.result);
      // Fire audit + requests in parallel. Both endpoints are role-gated
      // server-side; failures here don't block the detail view.
      try {
        const auditUrl = API_ENDPOINTS.getComplaintAuditLog.replace(
          ":complaint_id",
          id
        );
        const auditRes = await getData(auditUrl);
        setAuditLog(auditRes?.result?.audit_log || []);
      } catch (_) { /* swallow */ }
      try {
        const reqUrl = API_ENDPOINTS.getComplaintRequests.replace(
          ":complaint_id",
          id
        );
        const reqRes = await getData(reqUrl);
        setRequests(reqRes?.result?.requests || []);
      } catch (_) { /* swallow */ }
    } catch (err) {
      setError("Failed to load complaint details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchComplaint();
  }, [fetchComplaint]);

  const handleTriage = async () => {
    setActionLoading(true);
    setError("");
    setSuccess("");
    try {
      const url = API_ENDPOINTS.triageComplaint.replace(":complaint_id", id);
      await postData(url, {});
      setSuccess("Complaint triaged — you are now assigned.");
      fetchComplaint();
    } catch (err) {
      setError(err?.response?.data?.message || "Triage failed.");
    } finally {
      setActionLoading(false);
    }
  };

  // CMP-002 — n-round, multi-target evidence/details request.
  const handleRequestDetails = async () => {
    setActionLoading(true);
    setError("");
    setSuccess("");
    try {
      const url = API_ENDPOINTS.requestComplaintDetails.replace(
        ":complaint_id",
        id
      );
      await postData(url, {
        target: requestTarget,
        message: requestMessage.trim() || undefined,
      });
      const targetLabel =
        TARGET_OPTIONS.find((t) => t.value === requestTarget)?.label ||
        requestTarget;
      setSuccess(`Round requested from ${targetLabel}.`);
      setRequestMessage("");
      fetchComplaint();
    } catch (err) {
      setError(err?.response?.data?.message || "Request failed.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolutionSuccess = () => {
    setShowResolutionModal(false);
    setSuccess("Complaint resolved successfully.");
    fetchComplaint();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const InfoRow = ({ label, value }) => (
    <Box sx={{ display:"flex", alignItems:"flex-start", py:1, borderBottom:`1px solid ${T.border}`, "&:last-of-type":{ borderBottom:"none" } }}>
      <Typography sx={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.3px", minWidth:160, flexShrink:0 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize:13, fontWeight:600, color:T.text, flex:1 }}>
        {value || "—"}
      </Typography>
    </Box>
  );

  // CMP-002 — render an evidence card filtered by submitter role.
  // Customer-side has request_id === null (filing batch); owner/driver
  // and customer follow-up uploads carry request_id pointing at the
  // round that triggered them.
  const EvidenceCard = ({ title, evidences, accentColor }) => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: accentColor,
          }}
        />
        <Typography variant="subtitle1" fontWeight={600}>
          {title} ({evidences.length})
        </Typography>
      </Box>
      {evidences.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None submitted yet.
        </Typography>
      ) : (
        evidences.map((ev, idx) => (
          <Box key={ev.id} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              #{idx + 1} · {ev.submitted_by_user_type}
              {ev.request_id ? ` · request #${ev.request_id}` : " · filing"}
              {" · "}
              {formatDate(ev.submitted_at)}
            </Typography>
            {ev.description && (
              <Typography variant="body2" mb={0.5}>
                {ev.description}
              </Typography>
            )}
            {ev.file_url && (
              <a href={ev.file_url} target="_blank" rel="noreferrer">
                <img
                  src={ev.file_url}
                  alt={`Evidence ${idx + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: 160,
                    borderRadius: 4,
                    objectFit: "cover",
                    border: "1px solid #E5E7EB",
                    marginTop: 4,
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </a>
            )}
            {idx < evidences.length - 1 && <Divider sx={{ mt: 1.5 }} />}
          </Box>
        ))
      )}
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!complaint) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        Complaint not found.
      </Alert>
    );
  }

  // Backend (CMP-002) returns the evidence array under the `Evidence`
  // alias. Earlier code looked for `ComplaintEvidences` (a Sequelize
  // default that doesn't match this model's `as: "Evidence"`); that's
  // why photos never rendered before. Keep both as fallbacks for the
  // legacy alias shape.
  const allEvidence = complaint.Evidence || complaint.ComplaintEvidences || [];
  const customerEvidence = allEvidence.filter(
    (e) => e.submitted_by_user_type === "customer"
  );
  const partyEvidence = allEvidence.filter(
    (e) => e.submitted_by_user_type !== "customer"
  );

  const canTriage = complaint.status === "submitted";
  // Re-request allowed while the round loop is active.
  const canRequest =
    complaint.status === "under_review" ||
    complaint.status === "awaiting_response";
  const canResolve =
    complaint.status === "under_review" || complaint.status === "reopened";

    // SUT status → palette
    const STATUS_SUT = {
      submitted:         { bg:T.amberLt,  color:T.amber   },
      under_review:      { bg:T.indigoLt, color:T.indigo  },
      awaiting_response: { bg:T.tealLt,   color:T.teal    },
      resolved:          { bg:T.emeraldLt,color:T.emerald },
      closed:            { bg:"#F1F5F9",  color:T.muted   },
      reopened:          { bg:T.roseLt,   color:T.rose    },
      escalated:         { bg:T.roseLt,   color:T.rose    },
    };
    const sCfg = STATUS_SUT[complaint.status] || { bg:"#F1F5F9", color:T.muted };
    const slaBreached = complaint.triage_sla_breached || complaint.resolution_sla_breached;

    const SCard = ({ children, sx={} }) => (
      <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", mb:3, ...sx }}>
        {children}
      </Card>
    );
    const SHead = ({ icon, title, right }) => (
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:2 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1.2 }}>
          <Box sx={{ width:28, height:28, borderRadius:"8px", bgcolor:T.tealLt, display:"flex", alignItems:"center", justifyContent:"center" }}>
            {React.cloneElement(icon, { sx:{ fontSize:15, color:T.teal } })}
          </Box>
          <Typography sx={{ fontSize:14, fontWeight:700, color:T.text, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{title}</Typography>
        </Box>
        {right}
      </Box>
    );

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero header ── */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3, boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ bgcolor:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:"10px", color:"#fff", "&:hover":{ bgcolor:"rgba(255,255,255,0.25)" } }}>
            <ArrowBackRoundedIcon sx={{ fontSize:18 }} />
          </IconButton>
          <Typography sx={{ fontSize:13, color:"rgba(255,255,255,0.7)", fontWeight:600 }}>Complaints</Typography>
        </Box>
        <Box sx={{ display:"flex", alignItems:"center", gap:2, flexWrap:"wrap" }}>
          <Box sx={{ width:46, height:46, borderRadius:"12px", bgcolor:"rgba(255,255,255,0.18)", border:"1px solid rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <HeadsetMicRoundedIcon sx={{ fontSize:24, color:"#fff" }} />
          </Box>
          <Box>
            <Box sx={{ display:"flex", alignItems:"center", gap:1.5, flexWrap:"wrap", mb:0.4 }}>
              <Typography sx={{ fontSize:22, fontWeight:800, color:"#fff", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{complaint.complaint_ref}</Typography>
              <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5, px:1.2, py:0.3, borderRadius:"8px", bgcolor:sCfg.bg, border:`1px solid ${sCfg.color}22` }}>
                <Box sx={{ width:5, height:5, borderRadius:"50%", bgcolor:sCfg.color }} />
                <Typography sx={{ fontSize:11, fontWeight:700, color:sCfg.color, textTransform:"capitalize" }}>{complaint.status?.replace(/_/g," ")}</Typography>
              </Box>
              {slaBreached && (
                <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5, px:1.2, py:0.3, borderRadius:"8px", bgcolor:T.roseLt }}>
                  <Box sx={{ width:5, height:5, borderRadius:"50%", bgcolor:T.rose }} />
                  <Typography sx={{ fontSize:11, fontWeight:700, color:T.rose }}>SLA Breached</Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize:12, color:"rgba(255,255,255,0.7)" }}>
              {CATEGORY_LABELS[complaint.category] || complaint.category} · Filed {formatDate(complaint.createdAt)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error   && <Alert severity="error"   sx={{ mb:2, borderRadius:"12px" }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb:2, borderRadius:"12px" }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Grid container spacing={3}>
        {/* Left Column — Complaint Info, SLA, Resolution */}
        <Grid item xs={12} md={7}>
          <SCard><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
            <SHead icon={<HeadsetMicRoundedIcon />} title="Complaint Details" />
            <InfoRow
              label="Category"
              value={CATEGORY_LABELS[complaint.category] || complaint.category}
            />
            <InfoRow label="Priority" value={complaint.priority} />
            <InfoRow label="Filed At" value={formatDate(complaint.createdAt)} />
            <InfoRow label="Customer ID" value={complaint.customer_id} />
            <InfoRow label="Trip ID" value={complaint.trip_id} />
            <InfoRow label="Package ID" value={complaint.package_id} />
            {complaint.package_id && (
              <Box sx={{ py:1, borderBottom:`1px solid ${T.border}` }}>
                <Box
                  onClick={() => navigate(`/app/package-info/${encryptId(complaint.package_id)}`)}
                  sx={{
                    display:"inline-flex", alignItems:"center", gap:0.8,
                    px:1.5, py:0.6, borderRadius:"9px", cursor:"pointer",
                    bgcolor:T.tealLt, border:`1px solid ${T.teal}22`,
                    transition:"all 0.15s",
                    "&:hover":{ bgcolor:T.teal, "& *":{ color:"#fff !important" } },
                  }}
                >
                  <OpenInNewRoundedIcon sx={{ fontSize:14, color:T.teal }} />
                  <Typography sx={{ fontSize:12, fontWeight:700, color:T.teal, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                    View Package Details
                  </Typography>
                </Box>
              </Box>
            )}
            {/* CMP-002 — refund cap is the amount the customer paid
                for this package (sum of Payment rows on the backend).
                `package_value` on PackageDetails is an optional declared
                value and is usually NULL, so we prefer `package_paid_amount`
                and fall back to it only when present. */}
            {complaint.package_paid_amount != null && (
              <InfoRow
                label="Customer Paid"
                value={`₹${Number(complaint.package_paid_amount).toLocaleString(
                  "en-IN",
                  { maximumFractionDigits: 2 }
                )}`}
              />
            )}
            {/* CMP-002 — refund cap = paid − CPM premium. Shown
                separately so the admin can see the breakdown without
                opening the resolve modal. */}
            {complaint.refund_cap != null && (
              <InfoRow
                label="Refund Cap"
                value={
                  <>
                    ₹{Number(complaint.refund_cap).toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                    {Number(complaint.cpm_premium) > 0 && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        (excl. CPM premium ₹
                        {Number(complaint.cpm_premium).toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}
                        )
                      </Typography>
                    )}
                  </>
                }
              />
            )}
            <Box sx={{ mt:1.5, pt:1.5, borderTop:`1px solid ${T.border}` }}>
              <Typography sx={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.3px", mb:0.5 }}>Description</Typography>
              <Typography sx={{ fontSize:13, color:T.text }}>{complaint.description}</Typography>
            </Box>
          </CardContent></SCard>

          {/* CMP-002 — Cargo Protection (CPM) reconciliation panel.
              Renders only when the customer opted into protection for
              this package. Resolution endpoint blocks while a CPM
              claim is pending; admin reconciles approved payouts in
              the resolution note. */}
          {complaint.cpm_booking && (
            <CpmReconcilePanel
              booking={complaint.cpm_booking}
              claim={complaint.cpm_claim}
              formatDate={formatDate}
            />
          )}

          {/* SLA Info */}
          <SCard><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
            <SHead icon={<CheckCircleRoundedIcon />} title="SLA Tracking" />
            <InfoRow
              label="Triage Deadline"
              value={formatDate(complaint.triage_sla_deadline)}
            />
            <InfoRow
              label="Triage SLA Breached"
              value={complaint.triage_sla_breached ? "Yes ⚠️" : "No"}
            />
            <InfoRow
              label="Resolution Deadline"
              value={formatDate(complaint.resolution_sla_deadline)}
            />
            <InfoRow
              label="Resolution SLA Breached"
              value={complaint.resolution_sla_breached ? "Yes ⚠️" : "No"}
            />
            {complaint.evidence_deadline && (
              <InfoRow
                label="Latest Evidence Deadline"
                value={formatDate(complaint.evidence_deadline)}
              />
            )}
            {complaint.escalated_at && (
              <InfoRow
                label="Escalated At"
                value={formatDate(complaint.escalated_at)}
              />
            )}
          </CardContent></SCard>

          {/* Resolution Info */}
          {complaint.resolution_outcome && (
            <SCard><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
              <SHead icon={<CheckCircleRoundedIcon />} title="Resolution" />
              <InfoRow
                label="Outcome"
                value={`${complaint.resolution_outcome} — ${
                  OUTCOME_LABELS[complaint.resolution_outcome] || ""
                }`}
              />
              <InfoRow
                label="Refund Amount"
                value={
                  complaint.refund_amount != null
                    ? `₹${complaint.refund_amount}`
                    : undefined
                }
              />
              <InfoRow
                label="Payout Deduction Applied"
                value={complaint.payout_deduction_applied ? "Yes" : "No"}
              />
              <InfoRow
                label="Customer Accepted"
                value={
                  complaint.customer_accepted === null
                    ? "Pending"
                    : complaint.customer_accepted
                    ? "Accepted"
                    : `Rejected — ${complaint.reject_reason || ""}`
                }
              />
              {complaint.resolution_note && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    Resolution Note
                  </Typography>
                  <Typography variant="body2">
                    {complaint.resolution_note}
                  </Typography>
                </>
              )}
            </CardContent></SCard>
          )}
        </Grid>

        {/* Right Column — Actions + Evidence cards */}
        <Grid item xs={12} md={5}>
          {/* Admin Actions */}
          <SCard><CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
            <SHead icon={<PersonRoundedIcon />} title="Admin Actions" />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {canTriage && (
                <Button variant="contained" disableElevation fullWidth disabled={actionLoading} onClick={handleTriage}
                  sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:T.teal, "&:hover":{ bgcolor:T.tealDk } }}>
                  Triage (Assign to Me)
                </Button>
              )}
              {canRequest && (
                <Box
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: 1,
                    p: 2,
                    bgcolor: "#FAFAFA",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
                    Request more information
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="From"
                    value={requestTarget}
                    onChange={(e) => setRequestTarget(e.target.value)}
                    sx={{ mb: 1.5 }}
                  >
                    {TARGET_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={6}
                    label="Message (optional)"
                    placeholder="What do you need from them?"
                    value={requestMessage}
                    onChange={(e) => setRequestMessage(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  <Button variant="outlined" fullWidth disabled={actionLoading} onClick={handleRequestDetails}
                    sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, borderColor:T.teal, color:T.teal, "&:hover":{ bgcolor:T.tealLt } }}>
                    Send Request
                  </Button>
                  {requests.length > 0 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1.5 }}
                    >
                      {requests.length} round{requests.length > 1 ? "s" : ""}{" "}
                      sent so far
                    </Typography>
                  )}
                </Box>
              )}
              {canResolve && (
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  disabled={actionLoading}
                  onClick={() => setShowResolutionModal(true)}
                >
                  Resolve Complaint
                </Button>
              )}
              {!canTriage && !canRequest && !canResolve && (
                <Typography variant="body2" color="text.secondary">
                  No actions available for current status.
                </Typography>
              )}
            </Box>
          </CardContent></SCard>

          {/* CMP-002 — Two evidence cards. Customer-side photos sit on
              top (the filing-time batch is usually here); owner/driver
              uploads appear below. */}
          <EvidenceCard
            title="Customer Evidence"
            evidences={customerEvidence}
            accentColor={ACTOR_COLORS.customer}
          />
          <EvidenceCard
            title="Owner / Driver Evidence"
            evidences={partyEvidence}
            accentColor={ACTOR_COLORS.owner}
          />
        </Grid>

        {/* Full-width — request rounds + audit log timeline */}
        <Grid item xs={12}>
          {requests.length > 0 && <RequestHistoryPanel requests={requests} formatDate={formatDate} />}
          <AuditLogTimeline auditLog={auditLog} formatDate={formatDate} />
        </Grid>
      </Grid>

      {/* Resolution Modal */}
      {showResolutionModal && (
        <ResolutionModal
          complaintId={id}
          /* CMP-002 — refund cap excludes the CPM premium (which bought
             a separate insurance product). Backend computes this as
             package_paid_amount - cpm_premium and returns it as
             `refund_cap`. Fall back through paid → declared. */
          packageValue={
            complaint?.refund_cap ??
            complaint?.package_paid_amount ??
            complaint?.Package?.package_value
          }
          cpmPremium={Number(complaint?.cpm_premium) || 0}
          cpmClaim={complaint?.cpm_claim || null}
          onClose={() => setShowResolutionModal(false)}
          onSuccess={handleResolutionSuccess}
        />
      )}
    </Box>
  );
}

// ─── CMP-002 panels (kept inline; small + tightly coupled to detail view) ───

// CPM (Cargo Protection) reconciliation panel — only rendered when a
// ProtectionBooking exists for the package. Shows the booking summary,
// the linked claim status (if any), and a reconcile hint when the
// claim has paid out (admin should subtract the approved amount from
// the complaint refund).
function CpmReconcilePanel({ booking, claim, formatDate }) {
  const premium = Number(booking?.premium_amount) || 0;
  const coverage = Number(booking?.coverage_limit) || 0;
  const approved = Number(claim?.approved_amount) || 0;
  const claimPending =
    claim && ["filed", "under_review", "evidence_pending"].includes(claim.status);
  const claimApproved = claim?.status === "approved";
  const claimRejected = claim?.status === "rejected" || claim?.status === "closed";

  const claimChipColor = claimPending
    ? "warning"
    : claimApproved
    ? "success"
    : claimRejected
    ? "default"
    : "info";

  return (
    <Paper sx={{ p: 3, mb: 3, borderLeft: 4, borderColor: "info.main" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Cargo Protection (CPM)
        </Typography>
        <Chip
          size="small"
          label={`Plan: ${booking.plan_tier?.toUpperCase() || "—"}`}
          color={booking.status === "active" ? "primary" : "default"}
          variant="outlined"
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
          Premium Paid
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          ₹{premium.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
          Coverage Limit
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          ₹{coverage.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
        </Typography>
      </Box>
      {booking.certificate_ref && (
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
            Certificate
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {booking.certificate_ref}
          </Typography>
        </Box>
      )}

      {!claim && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Customer opted into protection but no CPM claim has been filed. You
          can resolve this complaint normally — the refund cap already excludes
          the premium (₹{premium.toLocaleString("en-IN")}).
        </Alert>
      )}

      {claim && (
        <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              Linked CPM Claim: {claim.claim_ref}
            </Typography>
            <Chip
              size="small"
              label={(claim.status || "").replace("_", " ").toUpperCase()}
              color={claimChipColor}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Category: {claim.category} · Filed {formatDate(claim.filed_at)}
            {claim.closed_at && ` · Closed ${formatDate(claim.closed_at)}`}
          </Typography>

          {claimPending && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              <strong>Resolution blocked.</strong> Wait for the CPM claim to be
              approved or rejected before resolving this complaint, otherwise
              the customer can be paid twice for the same loss.
            </Alert>
          )}
          {claimApproved && approved > 0 && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              <strong>CPM paid out ₹{approved.toLocaleString("en-IN")}</strong>{" "}
              ({claim.payout_status || "pending wallet credit"}). Reconcile the
              complaint refund by subtracting this amount and document in the
              resolution note.
            </Alert>
          )}
          {claimRejected && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              CPM claim was {claim.status}. No CPM payout; resolve the complaint
              based on service-quality merits alone.
            </Alert>
          )}
        </Box>
      )}
    </Paper>
  );
}

function RequestHistoryPanel({ requests, formatDate }) {
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        Evidence Request Rounds ({requests.length})
      </Typography>
      {requests.map((r) => {
        const targetLabel =
          TARGET_OPTIONS.find((t) => t.value === r.target)?.label || r.target;
        const statusColor =
          r.status === "fulfilled"
            ? "success"
            : r.status === "expired"
            ? "error"
            : "warning";
        return (
          <Box
            key={r.id}
            sx={{
              borderLeft: "3px solid #E5E7EB",
              pl: 2,
              py: 1.5,
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="body2" fontWeight={600}>
                Round {r.round_number}
              </Typography>
              <Chip label={targetLabel} size="small" />
              <Chip
                label={r.status}
                size="small"
                color={statusColor}
                sx={{ textTransform: "capitalize" }}
              />
              <Typography variant="caption" color="text.secondary">
                requested {formatDate(r.requested_at)} · deadline{" "}
                {formatDate(r.deadline)}
              </Typography>
            </Box>
            {r.message && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {r.message}
              </Typography>
            )}
            {r.fulfilled_at && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Fulfilled {formatDate(r.fulfilled_at)} by{" "}
                {r.fulfilled_by_user_type}
              </Typography>
            )}
          </Box>
        );
      })}
    </Paper>
  );
}

function AuditLogTimeline({ auditLog, formatDate }) {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        Audit Log ({auditLog.length})
      </Typography>
      {auditLog.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No events recorded yet.
        </Typography>
      ) : (
        auditLog.map((ev) => {
          const color = ACTOR_COLORS[ev.actor_user_type] || ACTOR_COLORS.system;
          return (
            <Box
              key={ev.id}
              sx={{
                display: "flex",
                gap: 2,
                mb: 1.5,
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: color,
                  mt: 0.75,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {ev.event_type.replace(/_/g, " ")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {/* CMP-002 — backend now decorates each row with
                        actor_name (resolved from customer / owner / driver
                        / admin tables). Fall back to "role #id" if the
                        column is missing on a legacy row. */}
                    by {ev.actor_name
                      ? ev.actor_name
                      : `${ev.actor_user_type || "system"}${
                          ev.actor_user_id != null
                            ? ` #${ev.actor_user_id}`
                            : ""
                        }`}{" "}
                    · {formatDate(ev.created_at)}
                  </Typography>
                </Box>
                {ev.message && (
                  <Typography variant="body2" color="text.secondary">
                    {ev.message}
                  </Typography>
                )}
                {ev.from_status && ev.to_status && ev.from_status !== ev.to_status && (
                  <Typography variant="caption" color="text.secondary">
                    {ev.from_status} → {ev.to_status}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })
      )}
    </Paper>
  );
}
